"use client";

import { useEffect, useRef, useState, VideoHTMLAttributes } from "react";

interface VideoAnimationProps extends VideoHTMLAttributes<HTMLElement> {
  video: string;
  poster?: string;
  mute?: boolean;
  autoplay?: boolean;
  fit?: "cover" | "contain";
  rounded?: boolean;
}

export default function VideoAnimation({
  video,
  poster,
  fit = "cover",
  rounded = false,
  ...props
}: VideoAnimationProps) {
  const videoElement = useRef<HTMLVideoElement>(null);
  // Defer setting `src` until the video is near the viewport, so a page with
  // many videos doesn't fetch them all upfront. A video that is display:none
  // (e.g. hidden below a breakpoint) never intersects, so it never loads.
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const video = videoElement.current;
    if (!video) return;

    // Only load/play while the video is actually visible (or about to be),
    // so pages with many autoplay videos don't fetch/decode/play all of
    // them at once off-screen.
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) setShouldLoad(true);
      },
      // threshold 0: a target taller than the root never reaches a fractional
      // ratio, which would leave it permanently without a `src`.
      { threshold: 0, rootMargin: "200px 0px" }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  // Drive playback from an effect rather than from the observer callback, so
  // `src` (set via `shouldLoad` below) is already committed to the DOM by the
  // time play() runs. Calling play() in the same tick as setShouldLoad would
  // reject on a source-less element and never retry.
  useEffect(() => {
    const video = videoElement.current;
    if (!video || !props?.autoplay) return;

    if (isVisible && shouldLoad) {
      video.play().catch((error) => {
        // handle video playback error
        console.error("Error playing video:", error);
      });
    } else if (!isVisible) {
      video.pause();
    }
  }, [isVisible, shouldLoad, props?.autoplay]);

  // For video options, even if they are boolean values,
  // if they are set to false, they are not passed to the video element.
  // This is done to ensure the option takes effect properly.
  // Note: autoplay is intentionally NOT passed as the native `autoPlay`
  // attribute — playback is driven entirely by the IntersectionObserver above.
  const videoOptions = {
    ...(props?.mute && { muted: true }),
    ...(props?.loop && { loop: true }),
    ...(props?.controls && { controls: true }),
  };

  return (
    <video
      ref={videoElement}
      src={shouldLoad ? video : undefined}
      preload={shouldLoad ? undefined : "none"}
      poster={
        poster ??
        "/images/pages/migrated/30d08a55cbed5860dbfea627da3dfea10d3b3780-1666x1200.jpg"
      }
      playsInline
      className={`-z-1 w-full ${
        fit === "contain" ? "h-auto object-contain" : "h-full object-cover"
      } ${rounded ? "rounded-lg" : ""}`}
      {...videoOptions}
    ></video>
  );
}
